import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
} from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import httpClient from '../services/httpClient';
import { useAuth } from '../context/AuthContext';
import PublicNavBar from './PublicNavBar';
import PublicFooter from './PublicFooter';

const SubscriptionPlans = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      fetchStripeConfig();
    }
  }, [isAuthenticated]);

  const fetchStripeConfig = async () => {
    try {
      const response = await httpClient.get('/v1/subscription/checkout/config');
      setPublishableKey(response.data.publishableKey);
    } catch (err) {
      console.error('Failed to fetch Stripe config:', err);
      setError('Failed to load payment configuration');
    }
  };

  const handleSubscribe = async () => {
    // Check if user is authenticated first
    if (!isAuthenticated) {
      // Redirect to login page with return URL
      navigate('/login?redirect=/subscription/plans');
      return;
    }

    if (!publishableKey) {
      setError('Payment system not configured. Please contact support.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create checkout session
      const response = await httpClient.post('/v1/subscription/checkout/create-session', {
        successUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: `${window.location.origin}/subscription/plans`,
      });

      const { sessionId } = response.data;

      // Redirect to Stripe Checkout
      const stripe = await loadStripe(publishableKey);
      const { error: stripeError } = await stripe.redirectToCheckout({ sessionId });

      if (stripeError) {
        setError(stripeError.message);
      }
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      
      // Check if authentication error
      if (err.response?.status === 401 || err.response?.data?.code === 'AUTHENTICATION_REQUIRED') {
        setError('Please log in to subscribe');
        setTimeout(() => {
          navigate('/login?redirect=/subscription/plans');
        }, 2000);
      } else {
        setError(err.response?.data?.error || 'Failed to start checkout process');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
    {!isAuthenticated && <PublicNavBar />}
    <Container maxWidth="lg" sx={{ py: 4, pt: isAuthenticated ? 4 : 12, flexGrow: 1 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Choose Your Plan
        </Typography>
        <Typography variant="h6" color="text.secondary">
          $15 per gateway per month after a 30-day trial. Checkout collects a card. You are not charged during the trial.
        </Typography>
      </Box>

      {!isAuthenticated && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Ready to get started?</strong> Create an account first. The button below takes you to register, then checkout collects a card for the trial.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4} justifyContent="center">
        {/* Basic Plan */}
        <Grid item xs={12} md={6} lg={4}>
          <Card
            elevation={3}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <Chip
              label="RECOMMENDED"
              color="primary"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
              }}
            />
            <CardContent sx={{ flexGrow: 1, pt: 6 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Basic Gateway Access
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  $15
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                  /month
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Perfect for single gateway deployment
              </Typography>

              <List>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="30-day trial, card collected, not charged until it ends" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Single gateway access" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Local AI processing" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Secure VPN connection" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Real-time alerts" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Web dashboard access" />
                </ListItem>
              </List>
            </CardContent>
            <CardActions sx={{ p: 3, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubscribe}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : (isAuthenticated ? 'Start 30-day trial' : 'Register and start trial')}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Free Trial Info */}
        <Grid item xs={12}>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              A card is required to start the trial. Cancel before day 30 and you are not charged.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              If you do not cancel, the plan continues at $15 per gateway per month.
              Large-vision-model checks are not included and are not a separate listed fee. They need a GPU you supply.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Container>
    <PublicFooter />
    </Box>
  );
};

export default SubscriptionPlans;

